import { AvatarStyle } from "components/Leaderboard";
import { StageConfig } from "lib/redux/analytics/stage-definitions";
import { jsonSafeValueToDate } from "./typing";
import { ScopeDomain } from "./consts";
import { UserGroup, UserGroupMembership } from "./db";


////////////////////////////////////////////
// AuthorizedUsersDataResult is the return type of `get-authorized-users`
////////////////////////////////////////////


export type UserDataResult = {
  user_id: string
  user_name: string
  team_id: string
  created_at: Date
  team_is_active: boolean
  can_dial: boolean
  autodialer_disabled: boolean
  default_prospect_recording: PROSPECT_RECORDING_SETTING | null
  default_prospect_streaming_transcription: PROSPECT_RECORDING_SETTING | null
  default_prospect_transcript_persistence: PROSPECT_RECORDING_SETTING | null
  email_frequency_setting: EMAIL_FREQUENCY_SETTING | null
  default_language: DEFAULT_LANGUAGE_SETTING | null
  additional_language: DEFAULT_LANGUAGE_SETTING | null
  leaderboard_avatar_name: string | null
  leaderboard_avatar_style: AvatarStyle | null
  autodialer_paid: boolean | null
  autodialer_weekly_limit: number | null
  callblitz_enabled: boolean | null
  linkedin_finder_disabled: boolean | null
  parallel_enabled: boolean | null
  linkedin_messaging_enabled: boolean | null
  defaults_to_native_engine: boolean | null
  defaults_to_trellus_line: boolean | null
  trellus_only_for_parallel: boolean | null
  mixed_use_dialing: boolean | null
  enqueue_engine_enabled: boolean | null
  inbound_priority: number | null
  can_use_new_trellus_dialer: boolean | null
  forwarding_number: string | null
  autodialer_force_enabled: boolean | null
  only_native_lines: boolean | null
}

export function jsonSafeToAuthorizedUserDataResult(value: Record<string, any>): UserDataResult {
  return {
    user_id: value.user_id,
    user_name: value.user_name,
    team_id: value.team_id,
    created_at: jsonSafeValueToDate(value.created_at),
    team_is_active: value.team_is_active,
    can_dial: value.can_dial,
    autodialer_disabled: value.autodialer_disabled,
    default_prospect_recording: value.default_prospect_recording,
    default_prospect_streaming_transcription: value.default_prospect_streaming_transcription,
    default_prospect_transcript_persistence: value.default_prospect_transcript_persistence,
    email_frequency_setting: value.email_frequency_setting,
    default_language: value.default_language,
    additional_language: value.additional_language,
    leaderboard_avatar_name: value.leaderboard_avatar_name,
    leaderboard_avatar_style: value.leaderboard_avatar_style,
    autodialer_paid: value.autodialer_paid,
    autodialer_weekly_limit: value.autodialer_weekly_limit,
    callblitz_enabled: value.callblitz_enabled,
    linkedin_finder_disabled: value.linkedin_finder_disabled,
    parallel_enabled: value.parallel_enabled,
    linkedin_messaging_enabled: value.linkedin_messaging_enabled,
    defaults_to_native_engine: value.defaults_to_native_engine,
    defaults_to_trellus_line: value.defaults_to_trellus_line,
    trellus_only_for_parallel: value.trellus_only_for_parallel,
    mixed_use_dialing: value.mixed_use_dialing,
    enqueue_engine_enabled: value.enqueue_engine_enabled,
    inbound_priority: value.inbound_priority,
    can_use_new_trellus_dialer: value.can_use_new_trellus_dialer,
    forwarding_number: value.forwarding_number,
    autodialer_force_enabled: value.autodialer_force_enabled,
    only_native_lines: value.only_native_lines
  }
}

export function jsonSafeToAuthorizedUsersDataResult(value: Record<string, any>[]): UserDataResult[] {
  return value.map((item) => jsonSafeToAuthorizedUserDataResult(item))
}

export type UserAuthDataResult = {
  user_id: string
  api_key: string
  email: string
}

export function jsonSafeToUserAuthDataResult(value: Record<string, any>): UserAuthDataResult {
  return {
    user_id: value['user_id'],
    api_key: value['api_key'],
    email: value['email']
  }
}

export type UserAndUserAuthResult = {
  'user': UserDataResult
  'user_auth': UserAuthDataResult
}

export function jsonSafeToUserAndUserAuthResult(value: Record<string, any>): UserAndUserAuthResult {
  return {
    user: jsonSafeToAuthorizedUserDataResult(value[0]),
    user_auth: jsonSafeToUserAuthDataResult(value[1]),
  }
}

export function jsonSafeToUserAndUserAuthResults(value: Record<string, any>[]): UserAndUserAuthResult[] {
  return value.map((item) => jsonSafeToUserAndUserAuthResult(item))
}

export enum SubscriptionType {
  FREE = 'FREE',
  BETA = 'BETA',
  TEAM = 'TEAM'
}

export enum RedactionPolicy {
  NONE = 'NONE',
  NUMBER_SEQUENCE = 'NUMBER_SEQUENCE'
}

export type TeamResult = {
  team_id: string
  team: string
  subscription_type: SubscriptionType
  has_salesfloor_access: boolean
  has_practice_bot_access: boolean
  exclusive_domain: string | null
  twilio_weekly_spend: number | null
  use_new_dialer: boolean | null
  twilio_account_sid: string | null
  custom_stage_definition: StageConfig | null,
  default_prospect_recording: PROSPECT_RECORDING_SETTING | null
  default_prospect_streaming_transcription: PROSPECT_RECORDING_SETTING | null
  default_prospect_transcript_persistence: PROSPECT_RECORDING_SETTING | null
  disable_default_triggers: boolean | null
  redaction_policy: RedactionPolicy | null
  default_language: DEFAULT_LANGUAGE_SETTING | null
  reporting_min_healthy_connect_rate: number | null
  autodialer_disabled: boolean | null
  dialing_seats: number | null  // paid seats when restricted by seat
  linkedin_finder_disabled: boolean | null
  notify_on_review_status_change: boolean | null
  provisioning_team_id: string | null

}

export function jsonSafeToTeamResult(value: Record<string, any>): TeamResult {
  return {
    team_id: value['team_id'],
    team: value['team'],
    subscription_type: value['subscription_type'],
    has_salesfloor_access: value['has_salesfloor_access'],
    has_practice_bot_access: value['has_practice_bot_access'],
    exclusive_domain: value['exclusive_domain'],
    twilio_weekly_spend: value['twilio_weekly_spend'],
    use_new_dialer: value['use_new_dialer'],
    twilio_account_sid: value['twilio_account_sid'] ?? null,
    custom_stage_definition: value['custom_stage_definition'] ? JSON.parse(value['custom_stage_definition']) : null,
    default_prospect_recording: value['default_prospect_recording'],
    default_prospect_streaming_transcription: value['default_prospect_streaming_transcription'],
    default_prospect_transcript_persistence: value['default_prospect_transcript_persistence'],
    disable_default_triggers: value['disable_default_triggers'],
    redaction_policy: value['redaction_policy'],
    default_language: value['default_language'],
    reporting_min_healthy_connect_rate: value['reporting_min_healthy_connect_rate'],
    autodialer_disabled: value['autodialer_disabled'],
    dialing_seats: value['dialing_seats'],
    linkedin_finder_disabled: value['linkedin_finder_disabled'],
    notify_on_review_status_change: value['notify_on_review_status_change'],
    provisioning_team_id: value['provisioning_team_id']
  }
}

export function jsonSafeToTeamResults(value: Record<string, any>[]): TeamResult[] {
  return value.map((item) => jsonSafeToTeamResult(item))
}

export type VisibleAccountsResult = {
  teams: TeamResult[]
  users: UserDataResult[]
}

export function jsonSafeToVisibleAccountsResult(value: Record<string, any>): VisibleAccountsResult {
  return {
    teams: jsonSafeToTeamResults(value.teams),
    users: jsonSafeToAuthorizedUsersDataResult(value.users)
  }
}////////////////////////////////////////////
// SettingsResult is the result of get settings
////////////////////////////////////////////

export enum PROSPECT_RECORDING_SETTING {
  ALL = 'ALL',
  ONE_PARTY_ONLY = 'ONE_PARTY_ONLY',
  DIALER = 'DIALER',
  NONE = 'NONE'
}

export enum EMAIL_FREQUENCY_SETTING {
  DAILY = 'DAILY',
  TWICE_A_WEEK = 'TWICE_A_WEEK',
  WEEKLY = 'WEEKLY',
  NONE = 'NONE'
}

export enum DEFAULT_LANGUAGE_SETTING {
  ENGLISH = 'EN_US',
  SPANISH = 'ES_LATIN',
  FRENCH = 'FRENCH',
  MULTI = 'MULTI'
}

export const LANGUAGE_SETTING_TO_HUMAN_READABLE: {
  [k in DEFAULT_LANGUAGE_SETTING]: string
} = {
  [DEFAULT_LANGUAGE_SETTING.ENGLISH]: 'English',
  [DEFAULT_LANGUAGE_SETTING.SPANISH]: 'Spanish',
  [DEFAULT_LANGUAGE_SETTING.FRENCH]: 'French',
  [DEFAULT_LANGUAGE_SETTING.MULTI]: 'Auto-detect'
}

export type SettingsUpdate = {
  is_team: boolean
  team?: string  // team name
  targeted_user_id?: string // user id you are targeting if not yourself
  default_prospect_recording?: PROSPECT_RECORDING_SETTING
  default_prospect_streaming_transcription?: PROSPECT_RECORDING_SETTING
  default_prospect_transcript_persistence?: PROSPECT_RECORDING_SETTING
  email_frequency?: EMAIL_FREQUENCY_SETTING
  default_language?: DEFAULT_LANGUAGE_SETTING
  additional_language?: DEFAULT_LANGUAGE_SETTING
  reporting_min_healthy_connect_rate?: number
  autodialer_disabled?: boolean
  callblitz_enabled?: boolean
  linkedin_finder_disabled?: boolean
  notify_on_review_status_change?: boolean
  custom_stage_definition?: StageConfig
  inbound_priority?: number | null
  forwarding_number?: string | null
}

export enum SUBSCRIPTION_TYPE {
  FREE = 'FREE',
  BETA = 'BETA',
  TEAM = 'TEAM'
}

export type SettingsResult = {
  subscription_type: SUBSCRIPTION_TYPE
  default_prospect_recording: PROSPECT_RECORDING_SETTING
  default_prospect_streaming_transcription: PROSPECT_RECORDING_SETTING
  default_prospect_transcript_persistence: PROSPECT_RECORDING_SETTING
  email_frequency: EMAIL_FREQUENCY_SETTING
  default_language: DEFAULT_LANGUAGE_SETTING
  additional_language: DEFAULT_LANGUAGE_SETTING | null
  reporting_min_healthy_connect_rate: number | null
  autodialer_disabled: boolean | null
  callblitz_enabled: boolean | null
  linkedin_finder_disabled: boolean | null
  notify_on_review_status_change: boolean | null
}

////////////////////////////////////////////
// ScopeReuslt is the result of get scope
////////////////////////////////////////////

export const USER_COVERAGE_ID_ALL = 'ALL'
export const ACCOUNT_COVERAGE_ID_ALL = 'ALL'
export const ACCOUNT_COVERAGE_ID_NA = 'NA'

export enum ScopeType {
  SESSIONS_READ = 'SESSIONS_READ',// read session (and related) data
  SESSIONS_WRITE = 'SESSIONS_WRITE',// write session (and related) data
  ADMIN_WRITE = 'ADMIN_WRITE',// administer configuration
  INFRASTRUCTURE_READ = 'INFRASTRUCTURE_READ',// read data about servers and such
  INFRASTRUCTURE_WRITE = 'INFRASTRUCTURE_WRITE'
}


export type Scope = {
  user_id: string
  scope_type: ScopeType
  scope_domain: ScopeDomain
  team_id: string | null
}

export type ScopeResult = Scope[]

export enum ScopeHolderType {
  USER = 'USER',
  USER_GROUP = 'USER_GROUP',
  TEAM = 'TEAM'
}

export enum ScopeUserCoverageType {
  USER = 'USER',
  TEAM = 'TEAM',
  USER_GROUP = 'USER_GROUP',
  ALL = 'ALL' // everything covered by any matching ExternalScope rows
}


export enum ScopeAccountCoverageType {
  TEAM = 'TEAM',
  EXTERNAL_ACCOUNT = 'EXTERNAL_ACCOUNT',
  TEAM_AND_AGENCIES = 'TEAM_AND_AGENCIES',// team + dials done by agencies for which team is the client
  ALL = 'ALL',// TEAM plus everything granted to this team in the ExternalScope table
  NA = 'NA' // not applicable, e.g. for admin write
}

export type ScopeV2 = {
  scope_type: ScopeType
  holder_type: ScopeHolderType
  holder_id: string
  holder_team_id: string
  user_coverage_type: ScopeUserCoverageType
  user_coverage_id: string // takes special value USER_COVERAGE_ID_ALL for corresponding type
  account_coverage_type: ScopeAccountCoverageType // # will be NA for scopes other than session_read
  account_coverage_id: string // takes special values ACCOUNT_COVERAGE_ID_ALL, ACCOUNT_COVERAGE_ID_NA for corresponding types
}

export type ExternalScope = {
  scope_type: ScopeType
  holder_team_id: string
  account_coverage_type: ScopeAccountCoverageType // only TEAM
  account_coverage_id: string // only TEAM
}

export type ScopeV2Result = {
  scopes: ScopeV2[]
}

export type AdminWriteTargetsResult = {
  team_ids: string[]
  user_group_ids: string[]
  user_ids: string[]
}

export type GetUserGroupResult = {
  groups: UserGroup[]
  memberships: UserGroupMembership[]
}

